import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

const prussian = '#003153';
const paper = '#f8f6f0';
const blue = '#1a5276';
const lt = '#c5d4e0';
const accent = '#0077b6';
const dim = '#7a8fa0';

function Corner({ pos }: { pos: string }) {
  const style: Record<string, unknown> = {
    position: 'absolute', width: 12, height: 12,
    borderColor: blue, borderStyle: 'solid', borderWidth: 0,
  };
  if (pos.includes('t')) { style.top = -1; style.borderTopWidth = 1.5; }
  else { style.bottom = -1; style.borderBottomWidth = 1.5; }
  if (pos.includes('l')) { style.left = -1; style.borderLeftWidth = 1.5; }
  else { style.right = -1; style.borderRightWidth = 1.5; }
  return <div aria-hidden style={style as React.CSSProperties} />;
}

export default function LandingV7() {
  return (
    <div className="doctif-page" style={{
      fontFamily: 'Overpass, sans-serif',
      background: paper,
      color: prussian,
      position: 'relative',
      backgroundImage: `
        linear-gradient(${prussian}08 1px, transparent 1px),
        linear-gradient(90deg, ${prussian}08 1px, transparent 1px)
      `,
      backgroundSize: '28px 28px',
    }}>
      <Helmet><title>doctif — Blueprint</title></Helmet>

      {/* Header */}
      <header className="doctif-shell" style={{ padding: '26px 0 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'Fira Code, monospace', fontSize: 24, fontWeight: 700 }}>doctif://</span>
          <nav className="doctif-chip-row">
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <a key={n} className="doctif-chip" href={`/${n}`} style={{
                background: n === 7 ? prussian : 'transparent',
                color: n === 7 ? '#fff' : prussian,
                border: `1px solid ${lt}`,
                fontFamily: 'Fira Code, monospace', fontSize: 11,
              }}>V{n}</a>
            ))}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="doctif-shell" style={{
        padding: '48px 0 68px',
        display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 36, alignItems: 'start',
      }}>
        <div>
          <p style={{
            fontFamily: 'Fira Code, monospace', fontSize: 11,
            color: accent, letterSpacing: '0.1em', marginBottom: 18,
          }}>// suite.clinique.v7</p>
          <h1 style={{
            fontFamily: 'Fira Code, monospace',
            fontSize: 'clamp(1.8rem, 4.5vw, 3.2rem)',
            fontWeight: 700, lineHeight: 1.15, marginBottom: 22,
          }}>
            Transparence<br />
            <span style={{ color: accent }}>architecturale</span><br />
            pour cabinets.
          </h1>
          <p style={{ color: dim, fontSize: 16, lineHeight: 1.7, maxWidth: 480, marginBottom: 28 }}>
            doctif expose chaque rouage. Agenda, dossiers, facturation — tout est visible, mesurable, documenté.
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <a href="/accounts/login/" style={{
              fontFamily: 'Fira Code, monospace', fontSize: 13, fontWeight: 700,
              background: prussian, color: '#fff', padding: '12px 22px',
              textDecoration: 'none', transition: 'opacity 0.2s',
            }}>$ init doctif</a>
            <a href="/booking" style={{
              fontFamily: 'Fira Code, monospace', fontSize: 13,
              background: 'transparent', color: prussian, padding: '12px 22px',
              textDecoration: 'none', border: `1px solid ${lt}`,
            }}>$ demo --view</a>
          </div>
        </div>

        {/* Blueprint schematic card */}
        <div style={{
          border: `1px solid ${lt}`, padding: 20,
          position: 'relative', background: '#fff',
        }}>
          <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />
          <p style={{ fontFamily: 'Fira Code, monospace', fontSize: 10, color: dim, marginBottom: 12, letterSpacing: '0.1em' }}>FIG.01 — FLUX CLINIQUE</p>
          <div style={{ display: 'grid', gap: 8 }}>
            {[
              { t: '08:30', l: 'Patient.confirm()', s: 'OK' },
              { t: '10:00', l: 'Dossier.complete()', s: 'OK' },
              { t: '13:15', l: 'Facture.send()', s: 'OK' },
              { t: '17:40', l: 'Relance.trigger()', s: 'PEND' },
            ].map(r => (
              <div key={r.t} style={{
                fontFamily: 'Fira Code, monospace', fontSize: 12,
                display: 'flex', justifyContent: 'space-between', gap: 8,
                padding: '6px 0', borderBottom: `1px dashed ${lt}`,
              }}>
                <span style={{ color: accent }}>{r.t}</span>
                <span style={{ color: prussian }}>{r.l}</span>
                <span style={{ color: r.s === 'OK' ? '#059669' : accent, fontWeight: 700 }}>[{r.s}]</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics */}
      <div style={{ borderTop: `1px solid ${lt}`, borderBottom: `1px solid ${lt}` }}>
        <div className="doctif-shell" style={{
          padding: '22px 0', display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, textAlign: 'center',
        }}>
          {[
            { v: '240+', l: 'instances.active' },
            { v: '61k', l: 'appointments.month' },
            { v: '34%', l: 'time.saved.ratio' },
          ].map(m => (
            <div key={m.l}>
              <div style={{ fontFamily: 'Fira Code, monospace', fontSize: 28, fontWeight: 700, color: prussian }}>{m.v}</div>
              <div style={{ fontFamily: 'Fira Code, monospace', fontSize: 10, color: dim, letterSpacing: '0.1em' }}>{m.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <section className="doctif-shell" style={{ padding: '68px 0 52px' }}>
        <p style={{ fontFamily: 'Fira Code, monospace', fontSize: 11, color: accent, marginBottom: 12 }}>// §2 — modules</p>
        <h2 style={{ fontFamily: 'Fira Code, monospace', fontSize: 'clamp(1.2rem, 3vw, 1.8rem)', fontWeight: 700, marginBottom: 44 }}>
          Quatre modules. <span style={{ color: accent }}>Zéro boîte noire.</span>
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
          {[
            { n: '§2.1', t: 'Agenda', d: "Algorithme d'optimisation des créneaux. Confirmations SMS intégrées.", co: accent },
            { n: '§2.2', t: 'Dossiers', d: 'Historique complet indexé. Recherche full-text sur notes et radios.', co: '#059669' },
            { n: '§2.3', t: 'Facturation', d: 'Pipeline automatisé: devis → validation → facture → relance.', co: '#d97706' },
            { n: '§2.4', t: 'Multi-cabinet', d: 'Architecture multi-tenant avec isolation stricte des données.', co: '#7c3aed' },
          ].map(f => (
            <article key={f.n} style={{
              border: `1px solid ${lt}`, padding: '24px 20px',
              position: 'relative', background: '#fff',
              transition: 'border-color 0.2s',
            }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = f.co)}
              onMouseLeave={e => (e.currentTarget.style.borderColor = lt)}
            >
              <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />
              <div style={{ fontFamily: 'Fira Code, monospace', fontSize: 10, color: f.co, marginBottom: 10, letterSpacing: '0.1em' }}>{f.n}</div>
              <h3 style={{ fontFamily: 'Fira Code, monospace', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{f.t}</h3>
              <p style={{ color: dim, fontSize: 13, lineHeight: 1.65 }}>{f.d}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="doctif-shell" style={{ paddingBottom: 68 }}>
        <div style={{
          border: `1px solid ${lt}`, padding: 20, position: 'relative', background: '#fff',
        }}>
          <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <p style={{ fontFamily: 'Fira Code, monospace', fontSize: 10, color: dim, letterSpacing: '0.1em' }}>FIG.02 — TABLEAU DE BORD · 25.03.2026</p>
            <div style={{ display: 'flex', gap: 6 }}>
              {['agenda', 'patients', 'billing'].map((t, i) => (
                <span key={t} style={{
                  fontFamily: 'Fira Code, monospace', fontSize: 10, padding: '3px 10px',
                  background: i === 0 ? prussian : 'transparent',
                  color: i === 0 ? '#fff' : dim,
                  border: `1px solid ${lt}`,
                }}>{t}</span>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { time: '08:30', patient: 'Benali, M.', type: 'CTR', status: 'CONFIRMED' },
              { time: '09:15', patient: 'Idrissi, F.', type: 'DET', status: 'CONFIRMED' },
              { time: '10:00', patient: 'Lahlou, K.', type: 'PRO', status: 'PENDING' },
            ].map(a => (
              <div key={a.time} style={{
                border: `1px dashed ${lt}`, padding: '12px 10px', position: 'relative',
              }}>
                <div style={{ fontFamily: 'Fira Code, monospace', fontSize: 11, color: accent, marginBottom: 4 }}>{a.time}</div>
                <div style={{ fontFamily: 'Fira Code, monospace', fontSize: 12, fontWeight: 700, marginBottom: 2 }}>{a.patient}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                  <span style={{ fontFamily: 'Fira Code, monospace', fontSize: 9, color: dim }}>{a.type}</span>
                  <span style={{
                    fontFamily: 'Fira Code, monospace', fontSize: 9, fontWeight: 700,
                    color: a.status === 'CONFIRMED' ? '#059669' : accent,
                  }}>[{a.status}]</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section style={{ background: prussian, color: '#fff' }}>
        <div className="doctif-shell" style={{ padding: '60px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <p style={{ fontFamily: 'Fira Code, monospace', fontSize: 11, color: accent, marginBottom: 10 }}>// §3 — pipeline</p>
            <h2 style={{ fontFamily: 'Fira Code, monospace', fontSize: 'clamp(1.2rem, 3vw, 1.8rem)', fontWeight: 700 }}>
              {'input → process → '}<span style={{ color: accent }}>output</span>
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              { s: '01', t: 'Réservation', d: 'Patient sélectionne créneau → validation auto → confirmation envoyée.' },
              { s: '02', t: 'Consultation', d: 'Checklist interactive → notes temps réel → actes codifiés.' },
              { s: '03', t: 'Suivi', d: 'Facture générée → relance programmée → rapport quotidien.' },
            ].map(w => (
              <div key={w.s} style={{
                border: '1px solid rgba(255,255,255,0.1)', padding: '24px 18px',
                transition: 'border-color 0.2s',
              }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = accent)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
              >
                <div style={{ fontFamily: 'Fira Code, monospace', fontSize: 10, color: accent, marginBottom: 8 }}>STEP_{w.s}</div>
                <h3 style={{ fontFamily: 'Fira Code, monospace', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{w.t}</h3>
                <p style={{ color: '#8faabb', fontSize: 13, lineHeight: 1.6 }}>{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="doctif-shell" style={{ padding: '60px 0 48px' }}>
        <p style={{ fontFamily: 'Fira Code, monospace', fontSize: 11, color: accent, marginBottom: 10, textAlign: 'center' }}>// §4 — feedback</p>
        <h2 style={{ fontFamily: 'Fira Code, monospace', fontSize: 'clamp(1rem, 2.5vw, 1.5rem)', fontWeight: 700, textAlign: 'center', marginBottom: 40 }}>
          Retours <span style={{ color: accent }}>terrain.</span>
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {[
            { q: "La transparence change tout. On voit exactement ce qui se passe dans le flux.", n: 'Dr. Khalil', cl: 'Al Madina — Casablanca' },
            { q: "L'architecture multi-tenant est solide. Zéro fuite entre cabinets.", n: 'Dr. Brahmi', cl: 'Corbei — Oujda' },
          ].map(t => (
            <div key={t.n} style={{
              border: `1px solid ${lt}`, padding: '22px 18px', background: '#fff', position: 'relative',
            }}>
              <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />
              <blockquote style={{ fontFamily: 'Fira Code, monospace', fontSize: 14, lineHeight: 1.55, margin: '0 0 16px', color: prussian }}>
                "{t.q}"
              </blockquote>
              <div style={{ fontFamily: 'Fira Code, monospace', fontSize: 11, color: dim }}>— {t.n} / {t.cl}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <div style={{ borderTop: `1px solid ${lt}`, borderBottom: `1px solid ${lt}` }}>
        <div className="doctif-shell" style={{ padding: '26px 0', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, textAlign: 'center' }}>
          {[
            { l: 'encryption', v: 'AES-256' },
            { l: 'hosting', v: 'MA-CLOUD' },
            { l: 'latency', v: '< 1s' },
            { l: 'support.sla', v: '< 2h' },
          ].map(t => (
            <div key={t.l}>
              <div style={{ fontFamily: 'Fira Code, monospace', fontSize: 20, fontWeight: 700 }}>{t.v}</div>
              <div style={{ fontFamily: 'Fira Code, monospace', fontSize: 10, color: dim }}>{t.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <section className="doctif-shell" style={{ padding: '60px 0', textAlign: 'center' }}>
        <h2 style={{ fontFamily: 'Fira Code, monospace', fontSize: 'clamp(1.2rem, 3vw, 2rem)', fontWeight: 700, marginBottom: 12 }}>
          $ doctif <span style={{ color: accent }}>--start</span>
        </h2>
        <p style={{ color: dim, maxWidth: 400, margin: '0 auto 24px', fontSize: 14, lineHeight: 1.6, fontFamily: 'Fira Code, monospace' }}>
          Sans engagement. Sans carte bancaire.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <a href="/accounts/login/" style={{
            fontFamily: 'Fira Code, monospace', fontSize: 13, fontWeight: 700,
            background: prussian, color: '#fff', padding: '13px 26px',
            textDecoration: 'none',
          }}>$ init --production</a>
          <a href="/booking" style={{
            fontFamily: 'Fira Code, monospace', fontSize: 13,
            background: 'transparent', color: prussian, padding: '13px 26px',
            textDecoration: 'none', border: `1px solid ${lt}`,
          }}>$ demo</a>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${lt}` }}>
        <div className="doctif-shell" style={{ padding: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'Fira Code, monospace', fontSize: 11, color: dim }}>doctif v7.0 // blueprint-utility</span>
          <div style={{ display: 'flex', gap: 18 }}>
            {['privacy', 'terms', 'contact'].map(l => (
              <a key={l} href="#" style={{ fontFamily: 'Fira Code, monospace', color: dim, fontSize: 11, textDecoration: 'none' }}>{l}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
